import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useOnboardingSync } from '../../hooks/useOnboardingSync';
import { broadcastOnboardingUpdate } from '../../lib/broadcast';

export default function OnboardingIndex() {
  const location = useLocation();
  const { user, onboardingStep, setOnboardingStep } = useAuthStore();

  // Enable cross-tab synchronization
  useOnboardingSync();

  // Determine the correct onboarding step and path
  const determineOnboardingStep = React.useCallback(() => {
    if (!user) {
      return '/auth';
    }

    // If user has completed onboarding, go to home or stored redirect
    if (user.onboarding_completed) {
      const redirectTo = sessionStorage.getItem('authRedirect') || '/';
      sessionStorage.removeItem('authRedirect');
      return redirectTo;
    }

    // Determine which onboarding step to start with based on missing fields
    let nextStep: string | null = null;
    let redirectPath = '/';

    // Strict sequential check for onboarding steps
    if (!user.full_name || !user.display_name) {
      nextStep = 'name';
      redirectPath = '/onboarding/name';
    } else if (!user.professions?.length) {
      nextStep = 'profession';
      redirectPath = '/onboarding/profession';
    } else if (!user.experience_level) {
      nextStep = 'experience';
      redirectPath = '/onboarding/experience';
    } else if (!user.location) {
      nextStep = 'location';
      redirectPath = '/onboarding/location';
    } else if (!user.goals) {
      nextStep = 'goals';
      redirectPath = '/onboarding/goals';
    } else if (!user.terms_accepted || !user.onboarding_completed) {
      nextStep = 'terms';
      redirectPath = '/onboarding/terms';
    }

    return { nextStep, redirectPath };
  }, [user]);

  useEffect(() => {
    const result = determineOnboardingStep();

    // If result is a string, it's a direct redirect path
    if (typeof result === 'string') {
      if (onboardingStep !== null) {
        setOnboardingStep(null);
      }
      return;
    }

    // Otherwise, update onboarding step if it's different
    const { nextStep, redirectPath } = result;
    if (nextStep !== onboardingStep) {
      setOnboardingStep(nextStep);
      broadcastOnboardingUpdate({
        onboardingStep: nextStep,
        user
      });
    }
  }, [user, onboardingStep, setOnboardingStep, determineOnboardingStep]);

  useEffect(() => {
    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Revalidate onboarding state when tab becomes visible
        if (user && !user.onboarding_completed) {
          const result = determineOnboardingStep();
          if (typeof result !== 'string') {
            const { nextStep } = result;
            if (nextStep !== onboardingStep) {
              setOnboardingStep(nextStep);
              broadcastOnboardingUpdate({
                onboardingStep: nextStep,
                user
              });
            }
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, onboardingStep, setOnboardingStep, determineOnboardingStep]);

  // Get the redirect path
  const result = determineOnboardingStep();
  const redirectPath = typeof result === 'string' ? result : result.redirectPath;

  // Only redirect if we're not already on the correct path
  if (location.pathname !== redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  // Return null if we're already on the correct path
  return null;
}